// Thin fetch wrappers for the auth endpoints. Cookies ride automatically with
// `credentials: 'same-origin'` since web + api share the same origin in both
// dev (Vite proxy) and prod (skena.trailpaint.org).

import type { CompanionId, PublicUser, QuickSignupResponse } from '@skena/schema'

interface ApiSuccess<T> {
  data: T
}
interface ApiError {
  error: { code: string; message: string; details?: unknown }
}
type ApiResult<T> = ApiSuccess<T> | ApiError

export class AuthError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new AuthError(err.code, err.message, res.status)
  }
  return json.data
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new AuthError(err.code, err.message, res.status)
  }
  return json.data
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new AuthError(err.code, err.message, res.status)
  }
  return json.data
}

export const authApi = {
  byToken: (token: string) => postJson<PublicUser>('/api/auth/by-token', { token }),
  login: (classCode: string, displayName: string) =>
    postJson<PublicUser>('/api/auth/login', { classCode, displayName }),
  quickSignup: (displayName: string) =>
    postJson<QuickSignupResponse>('/api/auth/quick-signup', { displayName }),
  me: () => getJson<PublicUser>('/api/me'),
  setCompanion: (companionId: CompanionId) =>
    patchJson<PublicUser>('/api/me/companion', { companionId }),
  logout: () => postJson<{ ok: true }>('/api/auth/logout', {}),
}
